import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { motion } from 'framer-motion';
import {
  AMBER,
  CONTACT_EMAIL,
  CTA,
  PERSONA_DOORS_SECTION_ID,
  scrollToPersonaDoors,
} from '../demoContent';

/** The closing band. Both exits lead somewhere real: back into the demo, or to a human. */
export default function DemoCta() {
  const theme = useTheme();

  const handleEnter = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    scrollToPersonaDoors();
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        color: 'common.white',
        textAlign: 'center',
        background: `linear-gradient(140deg, ${darken(theme.palette.primary.dark, 0.4)} 0%, ${
          theme.palette.primary.dark
        } 60%, ${theme.palette.primary.main} 100%)`,
        py: { xs: 9, md: 12 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 700,
          height: 700,
          borderRadius: '50%',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${alpha(AMBER.main, 0.12)} 0%, transparent 60%)`,
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative' }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <Typography
            component="p"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              color: AMBER.main,
              mb: 1.5,
            }}
          >
            {CTA.overline}
          </Typography>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700, letterSpacing: '-0.015em' }}>
            {CTA.heading}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              mt: 2,
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 640,
              mx: 'auto',
              color: alpha('#FFFFFF', 0.8),
            }}
          >
            {CTA.sub}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 5 }}
          >
            <Button
              size="large"
              variant="contained"
              endIcon={<ArrowUpwardIcon />}
              href={`#${PERSONA_DOORS_SECTION_ID}`}
              onClick={handleEnter}
              sx={{
                bgcolor: AMBER.main,
                color: AMBER.contrastText,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: AMBER.dark },
              }}
            >
              {CTA.primaryCta}
            </Button>
            <Button
              size="large"
              variant="outlined"
              startIcon={<MailOutlineIcon />}
              href={`mailto:${CONTACT_EMAIL}`}
              sx={{
                color: 'common.white',
                borderColor: alpha('#FFFFFF', 0.5),
                px: 3,
                '&:hover': { borderColor: 'common.white', bgcolor: alpha('#FFFFFF', 0.08) },
              }}
            >
              {CTA.secondaryCta}
            </Button>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}
